import {
    ADD_NEW_ACTION,
    ALL_ACTIONS,
    CREATE_ENGAGEMENT,
    DEFAULT_ERROR_MSG,
    DELETE_ENGAGEMENT,
    ENGAGE_SUCCESS_MSG,
    ENGAGEMENT_FLOW_TABS,
    ENGAGEMENT_MODAL_CONTENT,
    FINDINGS_TABLE_PAGINATION,
    SELECT_USERS_PLACEHOLDER_TEXT,
    TEST_ENGAGEMENT,
    TRIGGER_ENGAGEMENT,
    YES_DELETE_IT,
} from 'Core-utils/constants/constants';
import { CustomTab } from 'Components/molecules';
import { Table } from 'Components/organisms';
import { AgGridEngagementFlow } from 'Components/organisms/AgGridEngagementFlow';
import { EngagementModalVariant, ENGAGEMENT_FLOW_TABLE_HEADER } from './engagementFlowTable';
import { useEngagementMenuOptions } from 'Hooks/useEngagementMenuOptions';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import {
    ACTIVE_FLOWS_ROUTE,
    DEFAULT_ROUTE,
    NOT_FOUND_ROUTE,
    ACTIVE_WORKFLOWS_ROUTE,
    ACTIONS_ROUTE,
    DEFAULT_WORKFLOW_ROUTE,
} from 'Core-utils/routes';
import { NotFoundPage } from 'Components/pages';
import { Button } from 'react-bootstrap';
import { CreateEngagementModal } from 'Components/organisms/CreateEngagementModal';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    addAction,
    fetchServiceProfiles,
    getAllActions,
    getEngagementFlow,
    getGridData,
    getUserCohorts,
    testEngagement,
    updateEngagementFlow,
} from 'Apis/library';
import {
    AMP_SETTINGS,
    AuthRole,
    CohortDto,
    CustomActionDto,
    CustomActionUpsertDto,
    FilterCriteria,
    FlowSpecDto,
    FlowSpecStatusKind,
    FlowSpecUpsertDto,
    KIND,
    Page,
    ServiceProfile,
} from '@ampsec/platform-client';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import Actions from './Actions';
import { CustomActionsModal } from 'Components/organisms/CustomActionsModal';
import { CustomActionWizard } from 'Components/organisms/CustomActionWizard';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { RootState } from 'Rdx/store';
import { useSelector } from 'react-redux';
import { ConfirmationModal } from 'Components/molecules';
import PrivateRoute from 'Core-utils/PrivateRoute';
import './styles.scss';
import useTimeSpent from 'Hooks/useTimeSpent';
import { EngagementsModal } from 'Components/organisms/ActiveEngagementsModal';
import { TestEngagementModal } from 'Components/organisms/TestEngagementModal';
import ChipMultiSelect, { OptionType } from 'Components/molecules/ChipMultiSelect/ChipMultiSelect';
import { MultiValue } from 'react-select';
import { useConnectorData } from 'Hooks/useConnectorsData';
import { Typography } from 'Components/atoms';
import { getToastBody } from 'Core-utils/helpers/helpers';
import { useNavigate } from 'react-router-dom';
import { useI18n } from 'Hooks/useI18n';
import { useTenantSettings } from 'Hooks/useTenantSettings';
import { RuleBuilderModal } from 'Components/organisms/RuleBuilderModal';
import { RulesConfig } from 'Components/organisms/RulesConfig';

const COMPONENT_IDENTIFIER = 'Engagement Flow Grid';
const FLOW_SPEC_EXCLUDE_PRESET_SECURITY_TIP = 'securityTipFlowSpecs';

interface EngagementFlowTableProps {
    onDuplicate: (flowSpec: FlowSpecDto) => void;
}
// Disable no-unused-vars rule until we remove this table altogether
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const EngagementFlowTable: React.FC<EngagementFlowTableProps> = ({ onDuplicate }) => {
    const navigate = useNavigate();
    useTimeSpent(COMPONENT_IDENTIFIER);
    const [show, setShow] = useState<boolean>(false);
    const [refreshData, setRefreshData] = useState<boolean>(false);
    const [selectedId, setSelectedId] = useState<string>('');
    const { addToast } = useContext(ToastsContext);
    const [showEngagementsModal, setShowEngagementsModal] = useState(false);
    const [modalVariant, setEngagementModalVariant] = useState<EngagementModalVariant>('all');
    const [showTestEngagementModal, setShowTestEngagementModal] = useState(false);
    const [selectedOptions, setSelectedOptions] = useState<OptionType[]>([]);
    const [testFlowSpec, setTestFlowSpec] = useState<FlowSpecDto>({} as FlowSpecDto);
    const [searchText, setSearchText] = useState<string>('');
    const moreOptions = useEngagementMenuOptions();
    const { t } = useI18n();
    const { tenantSettings, settingsLoading } = useTenantSettings();
    const apiDataTransformer = (dataItem: Page<FlowSpecDto>) => {
        return dataItem.data;
    };
    const showMicrotrainingEngagements =
        (tenantSettings?.[AMP_SETTINGS.tenant.SHOW_MICROTRAINING_ENGAGEMENTS.id]?.value as boolean | undefined) ??
        AMP_SETTINGS.tenant.SHOW_MICROTRAINING_ENGAGEMENTS.defaultValue;
    const flowSpecFilterCriteria = useMemo<FilterCriteria>(
        () => ({
            limit: 250,
            status: [FlowSpecStatusKind.ACTIVE, FlowSpecStatusKind.PAUSED, FlowSpecStatusKind.GHOST],
            ...(showMicrotrainingEngagements ? {} : { excludePreset: FLOW_SPEC_EXCLUDE_PRESET_SECURITY_TIP }),
        }),
        [showMicrotrainingEngagements],
    );

    const { data } = useApiObjectData(
        'flowSpecs',
        getGridData,
        apiDataTransformer,
        flowSpecFilterCriteria,
        !settingsLoading,
    );
    const { connectorsByCategoryGroup } = useConnectorData();
    const { data: fetchUserCohorts } = useApiObjectData<CohortDto[], OptionType[]>(
        searchText,
        getUserCohorts,
        (dataItem) => {
            return (
                dataItem &&
                dataItem.map((item) => ({
                    id: item.value,
                    displayValue: item.displayValue,
                    value: item.value,
                    label: item.displayValue,
                    icon: connectorsByCategoryGroup?.['Identity & Access']?.[0]?.imageUrl,
                }))
            );
        },
    );
    const handleMoreActions = async (item: string, id: string) => {
        switch (item) {
            case 'DELETE':
                setShow(true);
                setSelectedId(id);
                break;
            case 'DUPLICATE':
                const duplicateData = getEngagementFlow('flowSpecs', { id }).then(
                    (res: Page<FlowSpecDto> | undefined) => {
                        if (!res || !res.data.length) {
                            return {} as FlowSpecDto;
                        }
                        return res.data[0];
                    },
                );

                duplicateData && onDuplicate(await duplicateData);
                break;
            case 'TEST':
                setShowTestEngagementModal(true);
                const res = await getEngagementFlow('flowSpecs', { id });
                if (res && res.data.length) {
                    setTestFlowSpec(res.data[0]);
                } else {
                    setTestFlowSpec({} as FlowSpecDto);
                }
                break;
            case 'ENGAGEMENT LOG':
                navigate(`${DEFAULT_WORKFLOW_ROUTE}/engagement-logs/${id}`);
                break;
            default:
                break;
        }
    };
    const handleUpdateStatus = async (id: string, value: FlowSpecStatusKind) => {
        try {
            const res = await getEngagementFlow('flowSpecs', { id });
            const fullSpec = res?.data?.[0] ?? data.find((item) => item.id === id) ?? ({} as FlowSpecDto);
            await updateEngagementFlow({ ...fullSpec, status: value });
            setRefreshData((refreshData) => !refreshData);
        } catch {
            addToast({ severity: 'error', body: DEFAULT_ERROR_MSG });
        }
    };
    const handleDeleteClick = async () => {
        try {
            const res = await getEngagementFlow('flowSpecs', { id: selectedId });
            const fullSpec = res?.data?.[0] ?? data.find((item) => item.id === selectedId) ?? ({} as FlowSpecDto);
            await updateEngagementFlow({ ...fullSpec, status: FlowSpecStatusKind.ARCHIVE });
            setRefreshData((refreshData) => !refreshData);
        } catch {
            addToast({ severity: 'error', body: DEFAULT_ERROR_MSG });
        }
        setShow(false);
    };
    const handleCloseModal = () => {
        setShow(false);
    };

    const handleCloseTestEngagementModal = () => {
        setSelectedOptions([]);
        setShowTestEngagementModal(false);
    };
    const handleTriggerButtonClick = () => {
        testEngagement(
            testFlowSpec,
            selectedOptions.map((option) => option.value),
        )
            .then((_response) => {
                addToast({
                    severity: 'success',
                    body: getToastBody(ENGAGE_SUCCESS_MSG),
                });
            })
            .catch((error) => {
                addToast({
                    severity: 'error',
                    body: DEFAULT_ERROR_MSG + error.message,
                });
            });
        setSelectedOptions([]);
        setShowTestEngagementModal(false);
    };
    const handleShowActiveEngagementsModal = (flowSpecId: string, modalVariant: EngagementModalVariant = 'all') => {
        setSelectedId(flowSpecId);
        setEngagementModalVariant(modalVariant);
        setShowEngagementsModal(true);
    };
    const handleCloseActiveEngagementsModal = () => setShowEngagementsModal(false);
    const handleOptionSelected = (selectedOptions: MultiValue<{ value: string; label: string }>) => {
        setSelectedOptions(selectedOptions as OptionType[]);
    };

    const handleSearchTextChange = (inputValue: string) => {
        setSearchText(inputValue);
    };

    return (
        <>
            <Table
                operation="_summary"
                filter={flowSpecFilterCriteria}
                fetchEnabled={!settingsLoading}
                columns={ENGAGEMENT_FLOW_TABLE_HEADER(
                    handleUpdateStatus,
                    handleMoreActions,
                    handleShowActiveEngagementsModal,
                    moreOptions,
                )}
                rowsPerPageOptions={FINDINGS_TABLE_PAGINATION}
                showSubHeader={false}
                tableBodyHeight={'calc(100% - 3.625rem)'}
                defaultSortId="name"
                cacheKey={'flowSpecs'}
                transformer={(a) => a}
                refreshTableData={refreshData}
                updateUrl={true}
            />
            <ConfirmationModal
                modalTitle={t(DELETE_ENGAGEMENT)}
                modalBodyTitle={t(ENGAGEMENT_MODAL_CONTENT)}
                onPrimaryButtonClick={handleDeleteClick}
                primaryButtonLabel={YES_DELETE_IT}
                show={show}
                onClose={handleCloseModal}
                buttonVariant="danger"
            />
            <EngagementsModal
                variant={modalVariant}
                show={showEngagementsModal}
                onClose={handleCloseActiveEngagementsModal}
                specId={selectedId}
            />
            <TestEngagementModal
                modalTitle={t(TEST_ENGAGEMENT)}
                onPrimaryButtonClick={handleTriggerButtonClick}
                primaryButtonLabel={TRIGGER_ENGAGEMENT}
                show={showTestEngagementModal}
                onClose={handleCloseTestEngagementModal}
                buttonVariant={'primary'}
                modalContent={
                    <div className="test-engagement-modal__multi-select-container">
                        <ChipMultiSelect
                            id={'id'}
                            selected={selectedOptions}
                            onChange={handleOptionSelected}
                            placeholder={
                                <Typography variant="body3" className="engagement-flow__placeholder mb-0">
                                    {SELECT_USERS_PLACEHOLDER_TEXT}
                                </Typography>
                            }
                            options={fetchUserCohorts}
                            onInputChange={handleSearchTextChange}
                        />
                    </div>
                }
                primaryButtonDisabled={selectedOptions.length === 0}
            />
        </>
    );
};

function EngagementFlowPage() {
    const { t } = useI18n();
    const { getAmpSettingValue: getTenantSetting } = useTenantSettings();
    const showRulesTab = getTenantSetting(AMP_SETTINGS.tenant.RULES_ENABLED.id) === true;
    const tabs = useMemo(
        () => ENGAGEMENT_FLOW_TABS.filter((tab) => tab.eventKey !== 'rules' || showRulesTab),
        [showRulesTab],
    );

    const [modals, setModals] = useState({
        showCreateEngagement: false,
        showCustomActions: {
            modal1: false,
            modal2: false,
        },
        showRuleBuilder: false,
    });

    const auth = useSelector((state: RootState) => state.auth);
    const [variant, setVariant] = useState<'all' | 'selected'>('selected');
    const [query, setQuery] = useState<string>('');
    const [editingRule, setEditingRule] = useState<any>(null);

    const [flowSpec, setFlowSpec] = useState<FlowSpecUpsertDto | undefined>(undefined);

    const [refreshData, setRefreshData] = useState<boolean>(false);
    const { data: actionsData } = useApiObjectData<CustomActionDto[], CustomActionDto[]>(
        KIND.CUSTOM_ACTIONS,
        getAllActions,
        (a) => a,
        { isTemplate: true, limit: 250 },
    );
    const [id, setId] = useState<string>('');
    const [serviceProfiles, setServiceProfiles] = useState<ServiceProfile[]>([]);

    useEffect(() => {
        fetchServiceProfiles().then((data: any) => setServiceProfiles(data));
    }, []);

    const location = useLocation();

    const buttonText = location.pathname === ACTIVE_WORKFLOWS_ROUTE ? CREATE_ENGAGEMENT : ADD_NEW_ACTION;

    const handleShow = () => {
        if (buttonText === CREATE_ENGAGEMENT) {
            setModals({ ...modals, showCreateEngagement: true });
        } else {
            const customActionsModal = { ...modals.showCustomActions, modal1: true, modal2: false };
            setModals({ ...modals, showCustomActions: customActionsModal });
            setVariant('all');
        }
    };

    const handleClose = () => {
        setModals({
            showCreateEngagement: false,
            showCustomActions: { modal1: false, modal2: false },
            showRuleBuilder: false,
        });
        setId('');
        setEditingRule(null);
    };

    const handleEditRule = (rule: any) => {
        setEditingRule(rule);
        setModals({ ...modals, showRuleBuilder: true });
    };

    const handleSearchBar = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(event.target.value);
    };

    const handleCardClick = async (id: string) => {
        const customActionsModal = { ...modals.showCustomActions, modal1: false, modal2: true };
        setModals({ ...modals, showCustomActions: customActionsModal });
        setId(id);
    };

    const handleSave = async (action: CustomActionUpsertDto) => {
        setModals((prev) => ({
            ...prev,
            showCustomActions: { modal1: false, modal2: false },
        }));
        setId('');

        try {
            await addAction({
                isTemplate: false,
                kind: 'REST_ACTION',
                retryStrategy: {
                    ...action.retryStrategy,
                    kind: 'CONSTANT_BACKOFF',
                    maxRetries: action.retryStrategy?.maxRetries || 0,
                    delay: action.retryStrategy?.delay || 0,
                },
                meta: {
                    ...action.meta,
                    request: {
                        ...action.meta.request,
                        payload: action.meta.request.payload && {
                            ...action.meta.request.payload,
                            kind: 'TEMPLATE',
                        },
                    },
                },
                description: action.description && action.description,
                displayValue: action.displayValue && action.displayValue,
            });
            setRefreshData((refreshData) => !refreshData);
        } catch {
            // Modals stay closed; user can reopen Add Action to retry.
        }
    };

    const handleDuplicate = (flowSpec: FlowSpecDto) => {
        setModals({ ...modals, showCreateEngagement: true });
        const name = `Clone - ${flowSpec.name} ${new Date().toISOString()}`;
        setFlowSpec((fspec) => ({ ...fspec, ...flowSpec, name }));
    };

    const allData: CustomActionDto[] = Array.isArray(actionsData) ? actionsData : [];

    return (
        <div className="d-flex flex-column engagement-flow__content h-100 p-3 gap-3">
            <div className="engagement-flow__tab-cont d-flex justify-content-between">
                <CustomTab tabs={tabs} />
                <Button
                    variant="primary-500"
                    className="engagement-flow__tab-cont__engagement_button body1 text-text-black"
                    onClick={handleShow}
                >
                    {t(buttonText)}
                </Button>
            </div>
            <Routes>
                <Route path={DEFAULT_ROUTE} element={<Navigate to={ACTIVE_FLOWS_ROUTE} />} />
                <Route
                    path={ACTIVE_FLOWS_ROUTE}
                    element={
                        <PrivateRoute
                            element={<AgGridEngagementFlow onDuplicate={handleDuplicate} />}
                            userRole={auth.user.role as unknown as AuthRole}
                            allowedRoles={[AuthRole.ADMIN, AuthRole.OWNER]}
                        />
                    }
                />
                {showRulesTab && (
                    <Route
                        path={'rules'}
                        element={
                            <PrivateRoute
                                element={<RulesConfig refresh={refreshData} onEditRule={handleEditRule} />}
                                userRole={auth.user.role as unknown as AuthRole}
                                allowedRoles={[AuthRole.ADMIN, AuthRole.OWNER]}
                            />
                        }
                    />
                )}
                {/* TODO:To be changed with Templates tab */}
                <Route
                    path={ACTIONS_ROUTE}
                    element={
                        <PrivateRoute
                            element={<Actions refresh={refreshData} serviceProfiles={serviceProfiles} />}
                            userRole={auth.user.role as unknown as AuthRole}
                            allowedRoles={[AuthRole.ADMIN, AuthRole.OWNER]}
                        />
                    }
                />

                <Route path={NOT_FOUND_ROUTE} element={<NotFoundPage />} />
            </Routes>
            {modals.showCreateEngagement && (
                <CreateEngagementModal show={modals.showCreateEngagement} onClose={handleClose} flowSpec={flowSpec} />
            )}
            <CustomActionsModal
                data={allData}
                show={modals.showCustomActions.modal1}
                onClose={handleClose}
                onCardClick={handleCardClick}
                variant={variant}
                query={query}
                onSearchQueryChange={handleSearchBar}
                title={ALL_ACTIONS}
                serviceProfileData={serviceProfiles}
            />
            <CustomActionWizard
                show={modals.showCustomActions.modal2}
                handleSave={handleSave}
                onClose={handleClose}
                id={id}
            />
            {showRulesTab && (
                <RuleBuilderModal
                    show={modals.showRuleBuilder}
                    onClose={handleClose}
                    rule={editingRule}
                    onSaved={() => setRefreshData((v) => !v)}
                />
            )}
        </div>
    );
}

export default EngagementFlowPage;
